// Accounts cache hook — SHARED global cache. First mount fetches; subsequent mounts reuse.
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";

let _cache = null;               // { items: [...], ts: number }
let _pending = null;             // in-flight promise (dedupe concurrent callers)
const _subs = new Set();         // subscriber setState fns
const TTL_MS = 30_000;           // 30s freshness window

async function _fetchOnce() {
  if (_pending) return _pending;
  _pending = api.get("/accounts").then(({ data }) => {
    _cache = { items: data.items || [], ts: Date.now() };
    _subs.forEach((fn) => fn({ items: _cache.items, loading: false }));
    return _cache.items;
  }).catch(() => {
    _cache = { items: [], ts: Date.now() };
    _subs.forEach((fn) => fn({ items: [], loading: false }));
    return [];
  }).finally(() => { _pending = null; });
  return _pending;
}

export function useAccounts() {
  const fresh = _cache && Date.now() - _cache.ts < TTL_MS;
  const [state, setState] = useState(fresh
    ? { items: _cache.items, loading: false }
    : { items: _cache?.items || [], loading: true });

  const load = useCallback(async () => { _cache = null; await _fetchOnce(); }, []);

  useEffect(() => {
    _subs.add(setState);
    if (!fresh) _fetchOnce();
    const on = (e) => { if (/accounts/.test(e?.detail?.url || "")) load(); };
    window.addEventListener("malat:data-changed", on);
    return () => { _subs.delete(setState); window.removeEventListener("malat:data-changed", on); };
  }, [fresh, load]);

  return { ...state, reload: load };
}
